import { useCallback, useEffect, useRef, useState } from 'react';
import { isEditableTarget } from './meetingSections.js';

// The meeting: the report as a click-through deck of the sections chosen for it.
//
// From the advisor lead's 8/19 ask: a presentation-style flow so an advisor who
// lacks the natural narrative arc gets one from the deliverable itself, showing
// one card at a time on a screen share instead of scrolling past nine of them.
// From the product side, it must not fork the report: the slides ARE the
// sections, so the meeting can never drift out of step with the scroll or the
// print, and a whiteboard keeps working inside its slide.
//
// What changed from the first version is which sections. It took every
// top-level section, so the deck was the whole report and the advisor clicked
// past the parts of it that belong to preparation. Now it takes the sections in
// the meeting list (meetingSections.js), in the order they appear on the page,
// and only the ones that rendered for this household.

export default function useMeeting({ include }) {
  const [slide, setSlide] = useState(null);
  const presenting = slide !== null;
  const includeRef = useRef(include);
  includeRef.current = include;

  const all = () => [...document.querySelectorAll('.asmt-doc > section[id]')];
  const sections = () => all().filter(el => includeRef.current.has(el.id));

  // Which sections exist on the page right now, so the panel can say which of
  // the list is available for this household. Read after every render and
  // kept as the same array when nothing changed, so it never loops.
  const [available, setAvailable] = useState([]);
  useEffect(() => {
    const ids = all().map(el => el.id);
    setAvailable(prev => (prev.join('\n') === ids.join('\n') ? prev : ids));
  });

  const stop = useCallback(() => setSlide(null), []);
  const start = useCallback(() => setSlide(0), []);
  const step = useCallback(delta => {
    setSlide(current => {
      if (current === null) return current;
      const max = sections().length - 1;
      return Math.min(max, Math.max(0, current + delta));
    });
  }, []);

  // Mark the active slide and read its area title for the chrome.
  const [context, setContext] = useState({ n: 0, count: 0, area: '' });
  useEffect(() => {
    const els = sections();
    // Every section on the page is marked, so the ones outside the meeting are
    // hidden along with the inactive ones while presenting.
    all().forEach(el => el.classList.toggle('asmt-slide-active', presenting && els[slide] === el));
    if (!presenting) return undefined;

    let area = '';
    let node = els[slide] || null;
    while (node) {
      node = node.previousElementSibling;
      if (node && node.classList.contains('asmt-area')) {
        area = node.querySelector('.asmt-area-title')?.textContent || '';
        break;
      }
    }
    setContext({ n: Math.min(slide + 1, els.length), count: els.length, area });

    const onKey = e => {
      // Never take a keystroke from something being typed into.
      if (isEditableTarget(e.target)) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); step(1); }
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); step(-1); }
      if (e.key === 'Escape') stop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [presenting, slide, step, stop, include]);

  return { presenting, slide, context, start, stop, step, available };
}
